import { Location } from '../types';

interface RouteInfo {
  distance: number;
  duration: number;
  isEstimate?: boolean;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const calculateStraightLineDistance = (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
): RouteInfo => {
  if (!window.ymaps) {
    const R = 6371;
    const dLat = (to.lat - from.lat) * Math.PI / 180;
    const dLon = (to.lng - from.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(from.lat * Math.PI / 180) * Math.cos(to.lat * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = Math.round(R * c);
    const duration = Math.round((distance / 60) * 60);
    return { distance, duration, isEstimate: true };
  }

  const distance = Math.round(
    window.ymaps.coordSystem.geo.getDistance(
      [from.lat, from.lng],
      [to.lat, to.lng]
    ) / 1000
  );
  const duration = Math.round((distance / 60) * 60);
  return { distance, duration, isEstimate: true };
};

export const calculateRoute = async (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
  retryCount = 0
): Promise<RouteInfo> => {
  return new Promise((resolve, reject) => {
    if (!window.ymaps) {
      console.warn('Yandex Maps API not loaded, using straight-line distance');
      resolve(calculateStraightLineDistance(from, to));
      return;
    }

    if (!isValidCoordinates(from) || !isValidCoordinates(to)) {
      console.error('Invalid coordinates provided');
      resolve(calculateStraightLineDistance(from, to));
      return;
    }

    window.ymaps.ready(() => {
      const timeout = setTimeout(() => {
        if (retryCount < MAX_RETRIES) {
          console.warn(`Route calculation timeout, retrying (${retryCount + 1}/${MAX_RETRIES})`);
          clearListeners();
          wait(RETRY_DELAY).then(() => {
            calculateRoute(from, to, retryCount + 1)
              .then(resolve)
              .catch(reject);
          });
        } else {
          console.warn('Route calculation failed after retries, using straight-line distance');
          resolve(calculateStraightLineDistance(from, to));
        }
      }, 10000);

      let multiRoute: ymaps.multiRouter.MultiRoute | null = null;

      const clearListeners = () => {
        if (multiRoute) {
          multiRoute.model.events.remove('requestsuccess');
          multiRoute.model.events.remove('requestfail');
        }
        clearTimeout(timeout);
      };

      try {
        multiRoute = new window.ymaps.multiRouter.MultiRoute(
          {
            referencePoints: [
              [from.lat, from.lng],
              [to.lat, to.lng],
            ],
            params: {
              routingMode: 'auto',
              avoidTrafficJams: true,
            },
          },
          {
            boundsAutoApply: false,
          }
        );

        multiRoute.model.events.add('requestsuccess', () => {
          const activeRoute = multiRoute?.getActiveRoute();
          clearListeners();

          if (!activeRoute) {
            console.warn('No route found, using straight-line distance');
            resolve(calculateStraightLineDistance(from, to));
            return;
          }

          try {
            const distance = Math.round(activeRoute.properties.get('distance').value / 1000);
            const duration = Math.round(activeRoute.properties.get('duration').value / 60);

            if (isNaN(distance) || isNaN(duration)) {
              console.warn('Invalid route data received, using straight-line distance');
              resolve(calculateStraightLineDistance(from, to));
              return;
            }

            resolve({ distance, duration, isEstimate: false });
          } catch (error) {
            console.error('Error extracting route data:', error);
            resolve(calculateStraightLineDistance(from, to));
          }
        });

        multiRoute.model.events.add('requestfail', (error: Error) => {
          clearListeners();
          console.error('Route calculation failed:', error);
          
          if (retryCount < MAX_RETRIES) {
            console.warn(`Retrying route calculation (${retryCount + 1}/${MAX_RETRIES})`);
            wait(RETRY_DELAY).then(() => {
              calculateRoute(from, to, retryCount + 1)
                .then(resolve)
                .catch(reject);
            });
          } else {
            console.warn('Route calculation failed after retries, using straight-line distance');
            resolve(calculateStraightLineDistance(from, to));
          }
        });
      } catch (error) {
        clearListeners();
        console.error('Error creating multiRoute:', error);
        resolve(calculateStraightLineDistance(from, to));
      }
    });
  });
};

const isValidCoordinates = (coords: { lat: number; lng: number }): boolean => {
  return (
    typeof coords.lat === 'number' &&
    typeof coords.lng === 'number' &&
    coords.lat >= -90 && coords.lat <= 90 &&
    coords.lng >= -180 && coords.lng <= 180
  );
};

const routeCache = new Map<string, RouteInfo>();
const CACHE_EXPIRY = 30 * 60 * 1000;

export const getCachedRoute = async (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
): Promise<RouteInfo> => {
  const cacheKey = `${from.lat},${from.lng}-${to.lat},${to.lng}`;
  
  const cached = routeCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const routeInfo = await calculateRoute(from, to);
    routeCache.set(cacheKey, routeInfo);
    
    setTimeout(() => {
      routeCache.delete(cacheKey);
    }, CACHE_EXPIRY);

    return routeInfo;
  } catch (error) {
    console.error('Error calculating route:', error);
    return calculateStraightLineDistance(from, to);
  }
};